import { Button } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./SparkLeaderBoard.css";
import crown from "../../../../../assets/crown.png";
import { getSparkStatus } from "../../../../../utils/SparkUtils";
import { useDispatch, useSelector } from "react-redux";
import { getLeaderboardData, sendEncouragement } from "../../../store/Spark/SparkThunks";

export default function SparkLeaderBoard({ onClose }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { localAvatar } = useSelector((state) => state.auth);

    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fixedPodium, setFixedPodium] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isFetchingNext, setIsFetchingNext] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const listRef = useRef(null);
    const podiumRef = useRef([]);

    const AVATAR_COLORS = [
        "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7",
        "#DDA0DD", "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E9",
        "#F8B500", "#6C5CE7", "#00B894", "#E17055", "#0984E3"
    ];

    useEffect(() => {
        podiumRef.current = fixedPodium
    }, [fixedPodium]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setSearchQuery(searchInput);
            setCurrentPage(1);
        }, 300);

        return () => clearTimeout(handler);
    }, [searchInput]);

    const handleSearchChange = (text) => {
        setSearchInput(text);
    };

    useEffect(() => {
        let ignore = false;
        let fetchPromise = null;

        const fetchLeaderboard = async () => {
            try {
                if (currentPage === 1) {
                    setIsLoading(true);
                } else {
                    setIsFetchingNext(true);
                }

                fetchPromise = dispatch(getLeaderboardData({
                    params: [{ page: currentPage }],
                    options: {
                        query: { search: searchQuery }
                    }
                }));

                const response = await fetchPromise.unwrap();

                if (ignore) return;

                if (!response?.data || !Array.isArray(response.data)) {
                    if (currentPage === 1) {
                        setLeaderboardData([]);
                        if (searchQuery.trim() === "") setFixedPodium([]);
                    }
                    setHasMore(false);
                    return;
                }

                const transformed = response.data
                    .filter(item => item?.user?.id)
                    .map(item => {
                        const userId = item.user.id;
                        return {
                            id: item.id,
                            userId,
                            firstName: item.user?.firstName?.trim() || "Inconnu",
                            lastName: item.user?.lastName?.trim() || "",
                            score: typeof item.sparksReceived === 'number' ? item.sparksReceived : 0,
                            can_send_encouragement: !item.encouragedByMe,
                            avatarColor: AVATAR_COLORS[userId % AVATAR_COLORS.length]
                        };
                    });

                if (currentPage === 1) {
                    const allCandidatesMap = new Map();

                    podiumRef.current.forEach(user => {
                        allCandidatesMap.set(user.userId, user);
                    });

                    transformed.forEach(user => {
                        allCandidatesMap.set(user.userId, user);
                    });

                    const allCandidates = Array.from(allCandidatesMap.values());
                    const newlySortedCandidates = allCandidates.sort((a, b) => b.score - a.score);
                    const newTop3 = newlySortedCandidates.slice(0, 3);
                    const newPodiumIds = newTop3.map(p => p.userId);

                    setFixedPodium(prevPodium => {
                        const prevString = prevPodium.map(p => `${p.userId}-${p.score}`).join(',');
                        const newString = newTop3.map(p => `${p.userId}-${p.score}`).join(',');
                        return prevString !== newString ? newTop3 : prevPodium;
                    });

                    const listToAdd = transformed.filter(user => !newPodiumIds.includes(user.userId));

                    setLeaderboardData(listToAdd);

                } else {
                    const currentPodiumIds = podiumRef.current.map(p => p.userId);
                    const listToAdd = transformed.filter(user => !currentPodiumIds.includes(user.userId));

                    setLeaderboardData(prev => [...prev, ...listToAdd]);
                }

                setTotalPages(response.totalPages || 1);
                setHasMore(currentPage < (response.totalPages || 1));

            } catch (error) {
                if (error.name === 'AbortError' || error.message === 'Aborted') {
                    return;
                }
                if (!ignore) console.error("❌ Erreur fetch:", error);
            } finally {
                if (!ignore) {
                    setIsLoading(false);
                    setIsFetchingNext(false);
                }
            }
        };

        fetchLeaderboard();

        return () => {
            ignore = true;
            if (fetchPromise) {
                fetchPromise.abort();
            }
        };
    }, [searchQuery, currentPage, dispatch]);

    const handleScroll = useCallback(() => {

        const container = listRef.current;

        if (!container) return;

        if (isLoading || isFetchingNext || !hasMore) {
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = container;
        const maxScroll = scrollHeight - clientHeight;

        if (maxScroll <= 0) return;

        const scrollPercentage = scrollTop / maxScroll;

        if (scrollPercentage >= 0.5) {
            setCurrentPage(prev => prev + 1);
        }
    }, [isLoading, isFetchingNext, hasMore]);

    useEffect(() => {

        const container = listRef.current;

        if (!container) return;

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const cancel = () => {
        onClose();
    };

    const handleEncouragementClick = async (userId) => {
        try {
            const response = await dispatch(sendEncouragement({ params: [{ targetId: userId }] })).unwrap();
           if (response?.status === 200 || response?.success) {
                setLeaderboardData(prev => prev.map(user =>
                    user.userId === userId
                        ? { ...user, can_send_encouragement: false }
                        : user
                ));
            }
        } catch (error) {
            console.error("❌ Erreur fetch:", error);
        }
    };

    return (
        <>
            <div className="Spark-Modal" onClick={cancel}>
                <div className="personal-Spark-modal-centered" onClick={(e) => e.stopPropagation()}>
                    <div className="Spark-leaderboard-Modal-header">
                        <div className="Spark-Modal-header-title">
                            <span className="Spark-leaderboard-Modal-header-title-text">
                                {t("spark_lead")}
                            </span>
                        </div>
                        <div className="Spark-leaderboard-modal-header-right">
                            <Button className="close-button" onClick={cancel}
                                style={{ padding: "0", margin: "0", maxWidth: "30px", width: "30px", minWidth: "20px" }}>
                                <span className="material-symbols-outlined">close</span>
                            </Button>
                        </div>
                    </div>

                    <div className="Spark-lead-modal-content">
                        {fixedPodium.length >= 3 ?
                            (<div className="Spark-lead-trio">
                                {fixedPodium[1] && (<div className="Spark-lead-second">
                                    <span className="Spark-lead-second-initials">
                                        {fixedPodium[1].firstName.charAt(0)}{fixedPodium[1].lastName.charAt(0)}
                                    </span>
                                    <span className="Spark-lead-name">
                                        <span className="Spark-lead-name-first">
                                            {fixedPodium[1].firstName}
                                        </span>
                                        {" "}{fixedPodium[1].lastName.charAt(0)}.
                                    </span>
                                    <span className="Spark-lead-score">
                                        {fixedPodium[1].score}
                                    </span>
                                    <div className="Spark-lead-second-podium">
                                        <span className="Spark-lead-second-podium-icon"> 2</span>
                                    </div>
                                </div>)}
                                {fixedPodium[0] && (
                                    <div className="Spark-lead-first">
                                        <img src={crown} className="Spark-leaderboard-crown" alt="Crown" />
                                        <span className="Spark-lead-first-initials">
                                            {fixedPodium[0].firstName.charAt(0)}{fixedPodium[0].lastName.charAt(0)}
                                        </span>
                                        <span className="Spark-lead-name">
                                            <span className="Spark-lead-name-first">
                                                {fixedPodium[0].firstName}
                                            </span>
                                            {" "}{fixedPodium[0].lastName.charAt(0)}.
                                        </span>
                                        <span className="Spark-lead-score">
                                            {fixedPodium[0].score}
                                        </span>
                                        <div className="Spark-lead-first-podium">
                                            <span className="Spark-lead-first-podium-icon"> 1</span>
                                        </div>
                                    </div>
                                )}
                                {fixedPodium[2] && (
                                    <div className="Spark-lead-third">
                                        <span className="Spark-lead-third-initials">
                                            {fixedPodium[2].firstName.charAt(0)}{fixedPodium[2].lastName.charAt(0)}
                                        </span>
                                        <span className="Spark-lead-name">
                                            <span className="Spark-lead-name-first">
                                                {fixedPodium[2].firstName}
                                            </span>
                                            {" "}{fixedPodium[2].lastName.charAt(0)}.
                                        </span>
                                        <span className="Spark-lead-score">
                                            {fixedPodium[2].score}
                                        </span>
                                        <div className="Spark-lead-third-podium">
                                            <span className="Spark-lead-third-podium-icon">
                                                3
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            )
                            : !isLoading ? (
                                <div className="Spark-lead-trio-empty">
                                    <span className="material-symbols-outlined">leaderboard</span>
                                    <span>{t("ranking_unavailable")}</span>
                                </div>
                            ) : null}
                        <div className="Spark-lead-content">
                            <div className="Spark-lead-Modal-search-container">
                                <input
                                    type="text"
                                    placeholder={t('search_participant')}
                                    className="Spark-Modal-search-input-conference"
                                    value={searchInput}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                />
                                <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                </svg>
                            </div>
                            <div className="Spark-leaderboard-list"
                                ref={listRef}>
                                {isLoading ? (
                                    <div className="Spark-leaderboard-loading">
                                        <span className="material-symbols-outlined loading-spinner">progress_activity</span>
                                        <span>{t("Loading...")}</span>
                                    </div>
                                ) :
                                    leaderboardData.length > 0 ? (
                                        <>
                                            {leaderboardData.map((user, index) => {
                                                const rank = 4 + index;
                                                const { icon, label } = getSparkStatus(user.score);
                                                return (
                                                    <div
                                                        key={user.id}
                                                        className="Spark-leaderboard-item"
                                                    >
                                                        <span className="Spark-leaderboard-rank">
                                                            {rank}
                                                        </span>

                                                        <span className="Spark-leaderboard-initials"
                                                            style={{ background: user.avatarColor }}>
                                                            {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                                                        </span>
                                                        <span className="Spark-leaderboard-fullname">
                                                            {user.firstName} {user.lastName}
                                                        </span>

                                                        {user.userId !== localAvatar?.id && (
                                                            <div className={`Spark-leaderboard-encouragement ${!user.can_send_encouragement ? 'disabled-encouragement' : ''}`} onClick={() => user.can_send_encouragement && handleEncouragementClick(user.userId)}>
                                                                <span className={`material-symbols-outlined spark-encouragement ${!user.can_send_encouragement ? 'disabled-encouragement' : ''}`}>
                                                                    thumb_up
                                                                </span>
                                                            </div>
                                                        )}

                                                        <div className="Spark-leaderboard-score-wrapper">
                                                            <img
                                                                src={icon}
                                                                alt={label}
                                                                className="Spark-leaderboard-spark-icon"
                                                                title={label}
                                                            />
                                                        </div>
                                                        <span className="Spark-leaderboard-score">
                                                            {user.score}
                                                        </span>


                                                    </div>
                                                );
                                            })}
                                            {isFetchingNext && (
                                                <div className="Spark-leaderboard-loading-more" style={{
                                                    display: 'flex', justifyContent: 'center', padding: '12px', color: '#6A7282', fontSize: '14px'
                                                }}>
                                                    <span className="material-symbols-outlined" style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }}>
                                                        progress_activity
                                                    </span>
                                                    {t("Loading...")}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="Spark-leaderboard-empty">
                                            {searchQuery.trim() ? t("No_results_for") + ` "${searchQuery}"` : t("no_participant")}
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}